import React, { useContext, useState } from 'react'

import IntlMessages from '../../../../util/IntlMessages'
import { StatusBadge } from '../../../../design-system/StatusBadge'
import { SelectField } from '../../../../design-system/SelectField'
import { IGetLabelsShort } from '../../../../types/labels'
import { CellShell, UsersTableEditingContext, emptyCellDisplay } from './EditableCell'

export interface ILabelsEditorProps {
  cellKey: string
  allLabels: IGetLabelsShort[]
  userLabels: IGetLabelsShort[]
  display: React.ReactNode
  editable: boolean
  lockReason?: string
  onToggle: (label: IGetLabelsShort, assigned: boolean) => Promise<void>
}

export const LabelsEditor = ({ cellKey, allLabels, userLabels, display, editable, lockReason, onToggle }: ILabelsEditorProps): React.ReactElement => {
  const { editingCell, setEditingCell } = useContext(UsersTableEditingContext)
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  const editing = editingCell === cellKey

  if (!editable) {
    return <CellShell display={display} lockReason={lockReason} />
  }

  const toggle = async (label: IGetLabelsShort, assigned: boolean) => {
    setPendingIds((previous) => new Set(previous).add(label.id))
    try {
      await onToggle(label, assigned)
    } finally {
      setPendingIds((previous) => {
        const next = new Set(previous)
        next.delete(label.id)
        return next
      })
    }
  }

  if (editing) {
    const selectedIds = userLabels.map((label) => label.id)

    const handleChange = (nextIds: string[]) => {
      const added = nextIds.find((id) => !selectedIds.includes(id))
      const removed = selectedIds.find((id) => !nextIds.includes(id))
      const changedId = added ?? removed
      if (!changedId || pendingIds.has(changedId)) {
        return
      }
      const label = allLabels.find((entry) => entry.id === changedId)
      if (label) {
        toggle(label, Boolean(added))
      }
    }

    return (
      <div className="vt-users-cell vt-users-cell--editing">
        <SelectField
          label={<IntlMessages id="app.labels" />}
          hideLabel
          autoFocus
          defaultOpen
          variant="borderless"
          mode="multiple"
          className="vt-users-labels-select"
          style={{ width: '100%' }}
          suffixIcon={null}
          popupMatchSelectWidth={false}
          maxTagCount="responsive"
          value={selectedIds}
          options={allLabels.map((label) => ({ value: label.id, label: label.name }))}
          notFoundContent={
            <div className="vt-users-labels-editor-hint">
              {allLabels.length === 0 ? <IntlMessages id="users.table.labelsCreatedElsewhere" /> : <IntlMessages id="users.table.noLabelsMatch" />}
            </div>
          }
          tagRender={({ value, closable, onClose }) => {
            const label = allLabels.find((entry) => entry.id === value)
            if (!label) {
              return <></>
            }
            return (
              <StatusBadge color={label.color} shape="pill" closable={closable} onClose={onClose}>
                {label.name}
              </StatusBadge>
            )
          }}
          onChange={handleChange}
          onDropdownVisibleChange={(open) => {
            if (!open) {
              setEditingCell(null)
            }
          }}
        />
      </div>
    )
  }

  return (
    <div
      className="vt-users-cell vt-users-cell--editable"
      role="button"
      tabIndex={0}
      onClick={() => setEditingCell(cellKey)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          setEditingCell(cellKey)
        }
      }}
    >
      <span className="vt-users-cell-value">{userLabels.length === 0 ? emptyCellDisplay(true, 'app.labels') : display}</span>
    </div>
  )
}
